const MAX_SEARCHES = 8;
const MAX_DISPLAY_SEARCHES = 4;
const LOCAL_STORAGE_KEY = 'recent-customer-searches';
export const RECENT_SEARCHES_EVENT = 'recent-customer-searches:update';
export const POLICY_INPUT_REGEX = /^(?:([A-Z]{2,3}))?(?:\(?\d{2}\)?)?(\d{8})$/i;

function safeParseArray(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function readAll(): string[] {
  try {
    return safeParseArray(localStorage.getItem(LOCAL_STORAGE_KEY));
  } catch {
    return [];
  }
}

function writeAll(list: string[]): void {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(list));
  } catch {
    // ignore
  }
}

function dispatchUpdate(fullList: string[]): void {
  try {
    window.dispatchEvent(
      new CustomEvent(RECENT_SEARCHES_EVENT, {
        detail: fullList.slice(0, MAX_DISPLAY_SEARCHES),
      }),
    );
  } catch {
    // ignore
  }
}

/**
 * Normalize a raw search term for storage (policy or first line of name).
 */
function normalizeForStorage(raw: string): string | null {
  const value = raw.trim();
  if (!value) return null;

  const match = POLICY_INPUT_REGEX.exec(value.toUpperCase());
  if (match) {
    const prefix = match[1] || '';
    const account = match[2];
    return `${prefix}${account}`;
  }

  return value.split('\r\n')[0];
}

/**
 * Extract normalized policy number (for performSearch branching).
 */
function extractPolicy(raw: string): string | null {
  const value = raw.trim();
  if (!value) return null;
  const match = POLICY_INPUT_REGEX.exec(value.toUpperCase());
  if (!match) return null;
  const prefix = match[1] || '';
  const account = match[2];
  return `${prefix}${account}`;
}

export function getRecentSearches(): string[] {
  return readAll().slice(0, MAX_DISPLAY_SEARCHES);
}

export function saveRecentSearch(rawSearchTerm: string): void {
  const normalized = normalizeForStorage(rawSearchTerm);
  if (!normalized) return;

  let existing = readAll();
  // De-duplicate (case-insensitive)
  existing = existing.filter((s) => s.toLowerCase() !== normalized.toLowerCase());
  existing.unshift(normalized);
  if (existing.length > MAX_SEARCHES) {
    existing = existing.slice(0, MAX_SEARCHES);
  }
  writeAll(existing);
  dispatchUpdate(existing);
}

export function removeRecentSearch(rawSearchTerm: string): void {
  const term = rawSearchTerm.trim();
  if (!term) return;
  let existing = readAll();
  const updated = existing.filter((s) => s.toLowerCase() !== term.toLowerCase());
  writeAll(updated);
  dispatchUpdate(updated);
}

export function performSearch(
  rawSearchTerm: string,
  openFn: (url: string, target?: string, features?: string) => Window | null = window.open,
): string | null {
  const value = rawSearchTerm.trim();

  if (!value || value.length < 2) {
    return 'Please enter at least 2 characters for a customer name.';
  }

  saveRecentSearch(value);

  const policy = extractPolicy(value);
  if (policy) {
    const url = `/search/policy-lookup?policyNumber=${encodeURIComponent(policy)}`;
    openFn(url, '', 'width=1100,height=800,left=3000,top=0');
  } else {
    const url = `/search/customer-lookup?insuredName=${encodeURIComponent(value)}`;
    openFn(url, '', 'width=1100,height=800,left=3000,top=0');
  }

  return null;
}

export function subscribeRecentSearches(listener: (items: string[]) => void): () => void {
  const customHandler = (e: Event) => {
    const detail = (e as CustomEvent<string[]>).detail;
    if (Array.isArray(detail)) {
      listener(detail.slice(0, MAX_DISPLAY_SEARCHES));
    } else {
      listener(getRecentSearches());
    }
  };
  window.addEventListener(RECENT_SEARCHES_EVENT, customHandler);

  const storageHandler = (e: StorageEvent) => {
    if (e.key === LOCAL_STORAGE_KEY) {
      listener(getRecentSearches());
    }
  };
  window.addEventListener('storage', storageHandler);

  return () => {
    window.removeEventListener(RECENT_SEARCHES_EVENT, customHandler);
    window.removeEventListener('storage', storageHandler);
  };
}
